import React, { useEffect, useRef, useState } from "react";
import { FiArrowRight, FiClock, FiMail, FiMapPin } from "react-icons/fi";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import PageMeta from "./PageMeta";
import "../css/contactus.scss";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  message: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Contact() {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const submitTimer = useRef(null);

  useEffect(
    () => () => {
      if (submitTimer.current) window.clearTimeout(submitTimer.current);
    },
    []
  );

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Enter your last name.";

    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!emailPattern.test(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.subject) nextErrors.subject = "Choose a subject.";

    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please add a little more detail (at least 10 characters).";
    }

    return nextErrors;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setShowSuccess(false);
      window.requestAnimationFrame(() => {
        document.getElementById(Object.keys(nextErrors)[0])?.focus();
      });
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    submitTimer.current = window.setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      setFormData(initialForm);
      submitTimer.current = null;
    }, 700);
  };

  const inputProps = (name) => ({
    name,
    value: formData[name],
    onChange: handleInputChange,
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${name}-error` : undefined,
  });

  return (
    <main className="contact-page">
      <PageMeta
        title="Contact"
        description={`Questions about an order or product? Contact the ${siteConfig.brandName} team.`}
      />

      <section className="contact-hero" aria-labelledby="contact-title">
        <div className="contact-shell contact-hero__inner">
          <p className="contact-eyebrow">Customer care</p>
          <h1 id="contact-title">Let&rsquo;s talk about good food.</h1>
          <p>
            Questions, pairing ideas or feedback—we would love to hear from you.
            Our team usually replies within one business day.
          </p>
        </div>
      </section>

      <section className="contact-main" aria-label="Contact details and enquiry form">
        <div className="contact-shell contact-grid">
          <aside className="contact-details">
            <p className="contact-eyebrow">Contact details</p>
            <h2>We&rsquo;re here to help.</h2>
            <p className="contact-details__intro">
              For an existing order, include the email used at checkout so we can
              find it quickly.
            </p>

            <address className="contact-list">
              <a href={`mailto:${siteConfig.contact.email}`}>
                <span><FiMail aria-hidden="true" /></span>
                <div><small>Email</small>{siteConfig.contact.email}</div>
              </a>
              <div>
                <span><FiMapPin aria-hidden="true" /></span>
                <div><small>Visit us</small>{siteConfig.contact.address}</div>
              </div>
              <div>
                <span><FiClock aria-hidden="true" /></span>
                <div><small>Customer care</small>{siteConfig.contact.hours}</div>
              </div>
            </address>

            <div className="contact-help-card">
              <p className="contact-eyebrow">Need a quick answer?</p>
              <h3>Delivery and returns, explained.</h3>
              <Link to="/shipping">Read the delivery guide <FiArrowRight aria-hidden="true" /></Link>
              <Link to="/returns">View our returns policy <FiArrowRight aria-hidden="true" /></Link>
            </div>
          </aside>

          <div className="contact-form-wrap">
            <div className="contact-form__heading">
              <p className="contact-eyebrow">Send a note</p>
              <h2>How can we help?</h2>
            </div>

            {showSuccess && (
              <div className="contact-alert contact-alert--success" role="status" aria-live="polite">
                <strong>Message received.</strong>
                <span>Thanks for getting in touch. Our team will reply soon.</span>
              </div>
            )}

            {Object.keys(errors).length > 0 && (
              <div className="contact-alert contact-alert--error" role="alert">
                <strong>Please check the highlighted fields.</strong>
                <span>We need a few more details before sending your message.</span>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="contact-form__row">
                <div className="contact-field">
                  <label htmlFor="firstName">First name <span aria-hidden="true">*</span></label>
                  <input id="firstName" type="text" autoComplete="given-name" {...inputProps("firstName")} />
                  {errors.firstName && <p id="firstName-error">{errors.firstName}</p>}
                </div>
                <div className="contact-field">
                  <label htmlFor="lastName">Last name <span aria-hidden="true">*</span></label>
                  <input id="lastName" type="text" autoComplete="family-name" {...inputProps("lastName")} />
                  {errors.lastName && <p id="lastName-error">{errors.lastName}</p>}
                </div>
              </div>

              <div className="contact-field">
                <label htmlFor="email">Email address <span aria-hidden="true">*</span></label>
                <input id="email" type="email" autoComplete="email" inputMode="email" {...inputProps("email")} />
                {errors.email && <p id="email-error">{errors.email}</p>}
              </div>

              <div className="contact-field">
                <label htmlFor="subject">Subject <span aria-hidden="true">*</span></label>
                <select id="subject" {...inputProps("subject")}>
                  <option value="">Choose a subject</option>
                  <option value="order">My order</option>
                  <option value="delivery">Delivery</option>
                  <option value="product">Product or pairing advice</option>
                  <option value="business">Business enquiry</option>
                  <option value="feedback">Feedback</option>
                </select>
                {errors.subject && <p id="subject-error">{errors.subject}</p>}
              </div>

              <div className="contact-field">
                <label htmlFor="message">Message <span aria-hidden="true">*</span></label>
                <textarea id="message" rows="7" {...inputProps("message")} />
                {errors.message && <p id="message-error">{errors.message}</p>}
              </div>

              <div className="contact-form__footer">
                <p>Fields marked * are required.</p>
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending…" : "Send message"}
                  {!isSubmitting && <FiArrowRight aria-hidden="true" />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Contact;
